import { Injector, inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AnalyticsService } from './analytics.service';
import { NotificationService } from 'src/app/services/notification.service';

import { AdminGuard } from './core/admin/admin.guard';

import { AuthGuard } from './entry/auth/auth.guard';
import { FeatureFlagService } from './services/feature-flag.service';

export const featureFlagGuard = () => (): boolean => {
  const featureFlagService = inject(FeatureFlagService);
  return !featureFlagService.isEnabled('mvp');
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./core/core.component').then((m) => m.CoreComponent),
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      },
      {
        path: 'home',
        loadComponent: () => import('./core/events').then((m) => m.EventsComponent),
        canActivate: []
      },
      {
        path: 'profile/:id',
        loadComponent: () => import('./core/profile').then((m) => m.ProfileComponent)
      },
      {
        path: 'identity/setup',
        loadComponent: () => import('./core/public-identity').then((m) => m.PublicIdentityComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'identity/edit',
        loadComponent: () => import('./core/public-identity').then((m) => m.PublicIdentityComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'identity/appeals',
        loadComponent: () => import('./core/identity-appeals').then((m) => m.IdentityAppealsComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'report/identity/:id',
        loadComponent: () => import('./core/identity-report').then((m) => m.IdentityReportComponent)
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./core/dashboard').then((m) => m.DashboardComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'setup',
        loadComponent: () => import('./core/setup').then((m) => m.SetupComponent)
      },
      {
        path: 'filter',
        loadComponent: () => import('./modals/filter-modal').then((m) => m.FilterModalComponent),
        canActivate: [featureFlagGuard()]
      },
      {
        path: 'position',
        loadComponent: () => import('./modals/position-modal').then((m) => m.PositionModalComponent),
        canActivate: [featureFlagGuard()]
      },
      {
        path: 'settings',
        loadComponent: () => import('./core/profile/settings').then((m) => m.SettingsComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'admin',
        loadComponent: () => import('./core/admin').then((m) => m.AdminComponent),
        canActivate: [AuthGuard, AdminGuard]
      },
      {
        path: 'city',
        loadComponent: () => import('./core/city').then((m) => m.CityComponent),
        canActivate: [featureFlagGuard()]
      },
      {
        path: 'city/:name',
        loadComponent: () => import('./core/city').then((m) => m.CityComponent),
        canActivate: [featureFlagGuard()]
      },
      {
        path: 'notification',
        loadComponent: () => import('./core/notifications').then((m) => m.NotificationComponent),
        canActivate: [AuthGuard, featureFlagGuard()]
      },
      {
        path: 'invite/:id',
        loadComponent: () => import('./core/invite').then((m) => m.InviteComponent),
        canActivate: [AuthGuard, featureFlagGuard()]
      },
      {
        path: 'map/:lat/:long',
        loadComponent: () => import('./core/events/map').then((m) => m.MapComponent)
      },
      {
        path: 'players',
        loadComponent: () => import('./core/players').then((m) => m.PlayersComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'event-details/:id',
        loadComponent: () => import('./core/events/event-details').then((m) => m.EventDetailsComponent)
      },
      {
        path: 'add-event',
        loadComponent: () => import('./core/events/create-event').then((m) => m.CreateEventComponent),
        canActivate: []
      },
      {
        path: 'edit-event/:id',
        loadComponent: () => import('./core/events/create-event').then((m) => m.CreateEventComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'events/:eventId/edit',
        loadComponent: () => import('./core/events/create-event').then((m) => m.CreateEventComponent),
        canActivate: [AuthGuard]
      },
      {
        // Alias: /events redirects to /home (preserving query params).
        // Must be declared after events/:eventId/edit to avoid shadowing it.
        path: 'events',
        redirectTo: ({ queryParams }): string => {
          const params = new URLSearchParams(queryParams as Record<string, string>);
          const qs = params.toString();
          return qs ? `home?${qs}` : 'home';
        }
      }
    ]
  },
  {
    path: 'auth/telegram/callback',
    loadComponent: () =>
      import('./entry/auth/telegram-oauth/telegram-callback.component').then((m) => m.TelegramCallbackComponent)
  },
  {
    path: 'auth/action',
    loadComponent: () => import('./entry/auth/action').then((m) => m.AuthActionComponent)
  },
  {
    path: 'login',
    loadComponent: () => import('./entry/auth/login').then((m) => m.LoginComponent)
  },
  {
    path: 'restore-account',
    loadComponent: () => import('./core/profile/restore-account').then((m) => m.RestoreAccountComponent)
  },
  {
    path: 'network-error',
    loadComponent: () => import('./entry/network-error').then((m) => m.NetworkErrorComponent)
  },
  {
    path: '404',
    loadComponent: () => import('./entry/empty').then((m) => m.EmptyComponent)
  },
  {
    path: '**',
    redirectTo: '404'
  }
];

export function navigationErrorHandlerFactory(injector: Injector): (error: unknown) => void {
  return (error: unknown): void => {
    const toast = injector.get(NotificationService);
    const analytics = injector.get(AnalyticsService);
    const router = injector.get(Router);

    analytics.trackNavigationError(error, router.url);

    if (!(error instanceof Error && error.message.includes('Cannot match any routes'))) {
      toast.show(
        $localize`:@@navigationError:A navigation error occurred. Please try again.`,
        $localize`:@@close:Close`
      );
    }
    // No redirect for 404, NotFoundComponent will be shown
  };
}
